import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";
import ScienceIcon from "@mui/icons-material/Science";
import { Alert, Box, Card, CardContent, Snackbar, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useAuthProvider } from "react-admin";
import LoadingButton from "@mui/lab/LoadingButton";
import { reportError } from "../backoffice.utils";
import { FirebaseError } from "firebase/app";
import { overviewCardStyle } from "./backofficeTheme";

const hostname = location.hostname;
const allowLoginAsTestUser = hostname === "localhost" || hostname.includes("dev");
const allowLoginWithCustomToken = location.search.includes("cl=1");

type PageState = "show buttons" | "login with email" | "login as test user" | "login with custom token";

type LoginMethod = "with Google" | "with Apple" | "with email + password" | "with custom token" | "as test user";

const externalLoginButtonSx = {
  color: "text.primary",
  borderColor: "divider",
  "&:hover": { borderColor: "text.secondary", bgcolor: "action.hover" },
};

export function LoginPage() {
  const theme = useTheme();
  const authProvider = useAuthProvider();
  const [pageState, setPageState] = useState<PageState>("show buttons");
  const [showLoginFailedAlert, setShowLoginFailedAlert] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [customToken, setCustomToken] = useState("");
  const [testUserEmail, setTestUserEmail] = useState("");
  const passwordInputRef = useRef<HTMLInputElement>();
  const [signingIn, setSigningIn] = useState<LoginMethod | "">("");
  useEffect(() => {
    if (pageState !== "show buttons") {
      const showButtonsWhenUserPressesEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setPageState("show buttons");
          event.preventDefault();
          event.stopPropagation();
        }
      };
      document.body.addEventListener("keydown", showButtonsWhenUserPressesEscape);
      return () => document.body.removeEventListener("keydown", showButtonsWhenUserPressesEscape);
    }
  }, [pageState]);

  const login = async (method: LoginMethod, fn: () => Promise<any>) => {
    setSigningIn(method);
    try {
      await fn();
      location.reload();
    } catch (error) {
      if (error instanceof FirebaseError && error.code === "auth/popup-closed-by-user") {
        return;
      }
      // Make brute force attacks harder by waiting a little bit here ...
      await new Promise((resolve) => setTimeout(resolve, 1000));
      reportError("login failed", error);
      setShowLoginFailedAlert(true);
    } finally {
      setSigningIn("");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Snackbar
        open={showLoginFailedAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={() => setShowLoginFailedAlert(false)}
      >
        <Alert onClose={() => setShowLoginFailedAlert(false)} severity="error" sx={{ width: "100%" }}>
          Anmeldung fehlgeschlagen
        </Alert>
      </Snackbar>
      <Card sx={{ ...overviewCardStyle, width: 480 }}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Stack style={{ alignItems: "stretch" }} spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <img src="/favicon.svg" width="48" height="48" style={{ borderRadius: "10px" }} />
              <Box>
                <Typography component="p" sx={{ m: "0 0 2px 0", color: "text.secondary", fontSize: "0.875rem" }}>
                  Willkommen zurück 👋🏻
                </Typography>
                <Typography component="h2" sx={{ m: 0, fontWeight: 600, fontSize: "1.25rem", lineHeight: 1.2 }}>
                  Einloggen im AUTOVIO Backoffice
                </Typography>
              </Box>
            </Box>
            <div style={{ marginBottom: "4px" }} />
            {(pageState === "show buttons" || pageState === "login with email") && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  label="E-Mail"
                  type="email"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      passwordInputRef.current?.focus();
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                />
                <TextField
                  inputRef={passwordInputRef}
                  margin="dense"
                  label="Passwort"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      void login("with email + password", () =>
                        authProvider.loginWithEmailAndPassword(email, password),
                      );
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          background: "none",
                          border: "none",
                          padding: "4px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                        tabIndex={-1}
                        type="button"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />
                        ) : (
                          <VisibilityIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />
                        )}
                      </button>
                    ),
                  }}
                />
              </>
            )}
            <LoadingButton
              key="login with email button"
              loading={signingIn === "with email + password"}
              disabled={!!signingIn || !email.trim() || !password.trim()}
              variant="contained"
              sx={{
                color: "primary.contrastText",
                backgroundColor: "primary.main",
                borderColor: "primary.main",
                "&:hover": { borderColor: "primary.dark", bgcolor: "primary.dark" },
                "&.Mui-disabled": {
                  color: "action.disabled",
                  backgroundColor: "action.disabledBackground",
                  borderColor: "action.disabledBackground",
                },
              }}
              startIcon={<EmailIcon />}
              onClick={() =>
                login("with email + password", () => authProvider.loginWithEmailAndPassword(email, password))
              }
            >
              Mit E-Mail einloggen
            </LoadingButton>
            {(pageState === "show buttons" || pageState === "login with email") && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box component="hr" sx={{ flex: 1, border: 0, borderTop: "1px solid", borderColor: "divider", m: 0 }} />
                <Typography
                  component="span"
                  sx={{ color: "text.secondary", fontSize: "0.75rem", fontWeight: 500, whiteSpace: "nowrap" }}
                >
                  ODER
                </Typography>
                <Box component="hr" sx={{ flex: 1, border: 0, borderTop: "1px solid", borderColor: "divider", m: 0 }} />
              </Box>
            )}
            {(pageState === "show buttons" || pageState === "login with email") && (
              <>
                <LoadingButton
                  key="login with Google button"
                  loading={signingIn === "with Google"}
                  disabled={!!signingIn}
                  variant="outlined"
                  sx={externalLoginButtonSx}
                  startIcon={<img src="/Google_G_logo.png" height="20" />}
                  onClick={() => login("with Google", () => authProvider.loginWithGoogle())}
                >
                  Weiter mit Google
                </LoadingButton>
                <LoadingButton
                  key="login with Apple button"
                  loading={signingIn === "with Apple"}
                  disabled={!!signingIn}
                  variant="outlined"
                  sx={externalLoginButtonSx}
                  startIcon={
                    <img
                      src="/Apple_logo.png"
                      height="20"
                      style={{ filter: theme.palette.mode === "dark" ? "brightness(0) invert(1)" : "brightness(0)" }}
                    />
                  }
                  onClick={() => login("with Apple", () => authProvider.loginWithApple())}
                >
                  Mit Apple einloggen
                </LoadingButton>
              </>
            )}
            {pageState === "login as test user" && (
              <TextField
                autoFocus
                margin="dense"
                label="E-Mail"
                type="email"
                fullWidth
                variant="standard"
                value={testUserEmail}
                onChange={(event) => setTestUserEmail(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    void login("as test user", () => authProvider.loginAsTestUser(testUserEmail));
                    event.preventDefault();
                    event.stopPropagation();
                  }
                }}
              />
            )}
            {allowLoginAsTestUser && (pageState === "show buttons" || pageState === "login as test user") && (
              <LoadingButton
                loading={signingIn === "as test user"}
                disabled={!!signingIn}
                variant="outlined"
                sx={externalLoginButtonSx}
                startIcon={<ScienceIcon />}
                onClick={
                  pageState === "show buttons"
                    ? () => setPageState("login as test user")
                    : () => login("as test user", () => authProvider.loginAsTestUser(testUserEmail))
                }
              >
                Als Testnutzer einloggen
              </LoadingButton>
            )}
            {pageState === "login with custom token" && allowLoginWithCustomToken && (
              <TextField
                margin="dense"
                label="Custom Token"
                autoFocus
                onChange={(event) => setCustomToken(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    void login("with custom token", () => authProvider.loginWithCustomToken(customToken));
                    event.preventDefault();
                    event.stopPropagation();
                  }
                }}
              />
            )}
            {allowLoginWithCustomToken && (pageState === "show buttons" || pageState === "login with custom token") && (
              <LoadingButton
                key="login with custom token button"
                loading={signingIn === "with custom token"}
                disabled={!!signingIn}
                variant="contained"
                startIcon={<SecurityIcon />}
                onClick={
                  pageState === "show buttons"
                    ? () => setPageState("login with custom token")
                    : () => login("with custom token", () => authProvider.loginWithCustomToken(customToken))
                }
              >
                Mit Custom Token einloggen
              </LoadingButton>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
